// src/services/hiddenMessages.js — messages this browser has hidden locally.
//
// 🟥 WHY THIS EXISTS AT ALL. "Delete for me" normally writes
//    `deletedFor: arrayUnion(myUid)` onto the message document, and every reader
//    honours it. But the server's store-and-forward sweep DELETES 1:1 message
//    documents once they have been delivered, so the message you are most likely
//    to want gone — the one showing "🔒 Encrypted Message", precisely because
//    this browser has no sealed copy of it — is often a message whose document is
//    already gone. `deleteForMe` returns false, and the only record of your
//    decision is local.
//
// 🟥 AND IT MUST STAY LOCAL. Delete-for-me is private: it is not an event, the
//    other person is never told, and it must never touch a shared field.
//    `chats/{id}.lastMessage` is shared — clearing it to fix my chat list would
//    blank the row in THEIR chat list too. So the record lives in this browser,
//    and the chat list is taught to read it rather than the chat doc being taught
//    to lie.
//
// 🟢 It was already being written, in ChatWindow's own useState/localStorage pair.
//    The only thing that changed is that it is now readable from outside the
//    component, which is what lets the row in the sidebar agree with the thread.

import { isGhostRow } from './ghostRows';

const KEY = (chatId) => `allo_hidden_${chatId}`;
const EVENT = 'allo:hidden-changed';

/* 🟢 A CACHE, BECAUSE THE CHAT LIST ASKS ON EVERY ROW OF EVERY RENDER.
   localStorage.getItem is synchronous and JSON.parse is not free; twenty rows
   re-rendering on every keystroke in the search box is a lot of both. The set is
   tiny and only this module can change it, so one Map is enough — and it is
   dropped for that chat the moment anything writes. */
const cache = new Map();

/** The ids this browser has hidden in one chat. Always a Set, never null. */
export function hiddenIdsFor(chatId) {
  if (!chatId) return new Set();
  const hit = cache.get(chatId);
  if (hit) return hit;
  let set;
  try {
    const raw = localStorage.getItem(KEY(chatId));
    const arr = raw ? JSON.parse(raw) : [];
    set = new Set(Array.isArray(arr) ? arr : []);
  } catch {
    /* private window, quota, a corrupted value — an empty set is the safe answer:
       it shows a message that should have been hidden, which is recoverable, in
       preference to throwing inside a snapshot handler, which is not. */
    set = new Set();
  }
  cache.set(chatId, set);
  return set;
}

/** Hide one message id in one chat, and tell anything watching. */
export function hideLocally(chatId, id) {
  if (!chatId || !id) return hiddenIdsFor(chatId);
  const next = new Set(hiddenIdsFor(chatId));
  next.add(id);
  try { localStorage.setItem(KEY(chatId), JSON.stringify([...next])); } catch { /* full */ }
  cache.set(chatId, next);
  /* 🟢 localStorage fires `storage` only in OTHER tabs, never the one that wrote.
     The chat list lives in this tab, so without an event of our own the row would
     not refresh until something else happened to re-render it. */
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: { chatId, id } })); } catch { /* SSR */ }
  return next;
}

/** Subscribe to hides — this tab's, and any other tab's. */
export function onHiddenChange(fn) {
  const local = (e) => fn(e?.detail?.chatId || null);
  const cross = (e) => {
    if (typeof e?.key === 'string' && e.key.startsWith('allo_hidden_')) {
      const id = e.key.slice('allo_hidden_'.length);
      cache.delete(id);            // another tab wrote it; ours is stale
      fn(id);
    }
  };
  window.addEventListener(EVENT, local);
  window.addEventListener('storage', cross);
  return () => {
    window.removeEventListener(EVENT, local);
    window.removeEventListener('storage', cross);
  };
}

/**
 * Is this message invisible to me, for any reason?
 * The one place that knows all of them, so the thread and the chat list cannot
 * disagree about what is on screen.
 */
export function isHiddenForMe(m, myUid, hidden) {
  if (!m) return true;
  if (hidden && hidden.has(m.id)) return true;                       // swept, hidden here
  if (Array.isArray(m.deletedFor) && m.deletedFor.includes(myUid)) return true;
  /* side-effect records, not messages — a vote or a reaction is the newest
     document in a chat far more often than it is the newest MESSAGE. One
     predicate, shared with the phone (services/ghostRows.js). */
  if (isGhostRow(m)) return true;
  return false;
}
